import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Divider,
    Paper,
    Container,
    Button,
    useMediaQuery,
    useTheme,
    IconButton,
    Tooltip
} from "@mui/material";
import {
    Schedule,
    Person,
    ArrowBack,
    Add,
    Download,
    Edit,
    CalendarToday,
    Chair,
    Topic
} from "@mui/icons-material";
import { Link, useHistory } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const ConferenceSessionsList = () => {
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const eventid = localStorage.getItem("eventid");
    const eventName = localStorage.getItem("eventname");
    const [sessions, setSessions] = useState([]);

    useEffect(() => {
        axios
            .get(`/api/get-conference-session-agenda?id=${eventid}`)
            .then((res) => {
                if (res.data.status === 200) {
                    setSessions(res.data.sessions);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch sessions:", err);
            });
    }, [eventid]);

    const downloadAgendaPDF = () => {
        const fileName = eventName ? `${eventName}-Agenda.pdf` : "conference-agenda.pdf";
        const agendaSection = document.getElementById("agenda-section");
        const backButton = document.getElementById("back-button");

        // Hide elements during PDF generation
        if (backButton) backButton.style.display = "none";
        agendaSection.classList.add("hide-edit-buttons");

        html2canvas(agendaSection, {
            scale: 2,
            backgroundColor: "#f9fafc",
            logging: false,
            useCORS: true
        }).then((canvas) => {
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            pdf.save(fileName);

            // Restore elements after PDF generation
            if (backButton) backButton.style.display = "flex";
            agendaSection.classList.remove("hide-edit-buttons");
        });
    };

    // Group sessions by date
    const groupSessionsByDate = () => {
        return sessions.reduce((acc, session) => {
            if (!acc[session.date]) {
                acc[session.date] = [];
            }
            acc[session.date].push(session);
            return acc;
        }, {});
    };

    const groupedSessions = groupSessionsByDate();

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: '#f9fafc',
            py: 4,
            marginTop: isMobile ? 0 : '100px',
            marginLeft: '10%'
        }}>
            <Container maxWidth="lg">
                {/* Back Button (outside agenda-section so it doesn't appear in PDF) */}
                <Box id="back-button" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton
                        onClick={() => history.goBack()}
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark
                            }
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                </Box>

                {/* Action Buttons (outside agenda-section) */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    mb: 4,
                    flexWrap: 'wrap'
                }}>
                    <Tooltip title="Add new session">
                        <Button
                            startIcon={<Add />}
                            color="primary"
                            variant="contained"
                            component={Link}
                            to="/admin/conference/add-session-form"
                            sx={{
                                minWidth: isMobile ? '100%' : 'auto',
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Add Session
                        </Button>
                    </Tooltip>

                    <Tooltip title="Download agenda as PDF">
                        <Button
                            startIcon={<Download />}
                            color="secondary"
                            variant="contained"
                            onClick={downloadAgendaPDF}
                            sx={{
                                minWidth: isMobile ? '100%' : 'auto',
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Download Agenda
                        </Button>
                    </Tooltip>
                </Box>

                {/* Agenda Content (everything inside this will be in the PDF) */}
                <Box id="agenda-section" sx={{
                    backgroundColor: 'white',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
                    p: 4,
                    position: 'relative'
                }}>
                    {/* Header Section (now inside agenda-section) */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 4,
                        gap: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h4" sx={{
                                fontWeight: 700,
                                color: theme.palette.primary.dark
                            }}>
                                Conference Agenda
                            </Typography>
                        </Box>

                        <Typography variant="h5" sx={{
                            fontWeight: 'bold',
                            color: theme.palette.secondary.main,
                            textAlign: isMobile ? 'center' : 'right'
                        }}>
                            {eventName}
                        </Typography>
                    </Box>

                    {sessions.length === 0 ? (
                        <Typography
                            variant="body1"
                            align="center"
                            sx={{
                                py: 4,
                                color: 'text.secondary'
                            }}
                        >
                            No sessions found. Add your first session to get started!
                        </Typography>
                    ) : (
                        Object.entries(groupedSessions).map(([date, dateSessions]) => (
                            <Box key={date} sx={{ mb: 6 }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3,
                                    p: 2,
                                    backgroundColor: theme.palette.primary.light,
                                    borderRadius: '8px',
                                    color: 'white'
                                }}>
                                    <CalendarToday sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {date}
                                    </Typography>
                                </Box>

                                {dateSessions.map((session) => (
                                    <Card
                                        key={session.id}
                                        sx={{
                                            mb: 4,
                                            borderRadius: 3,
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 6px 24px rgba(0,0,0,0.12)'
                                            }
                                        }}
                                    >
                                        <CardContent>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="flex-start"
                                                sx={{ mb: 2 }}
                                            >
                                                <Box>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: theme.palette.primary.main,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}
                                                    >
                                                        {session.session_category?.conference_session_category_name || "General Session"}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        color: 'text.secondary',
                                                        mt: 0.5
                                                    }}>
                                                        <Schedule fontSize="small" sx={{ mr: 1 }} />
                                                        {session.start_time} - {session.end_time}
                                                    </Typography>
                                                </Box>

                                                <Tooltip title="Edit session">
                                                    <Button
                                                        startIcon={<Edit />}
                                                        size="small"
                                                        variant="outlined"
                                                        component={Link}
                                                        to={`/admin/conference/edit-session-form/${session.id}`}
                                                        sx={{
                                                            borderRadius: '20px',
                                                            textTransform: 'none'
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                </Tooltip>
                                            </Box>

                                            {session.session_category?.chairmen?.length > 0 && (
                                                <Box sx={{
                                                    backgroundColor: '#f5f7fb',
                                                    borderRadius: '8px',
                                                    p: 2,
                                                    mb: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <Chair color="primary" />
                                                    <Box>
                                                        <Typography variant="caption" sx={{
                                                            fontWeight: 600,
                                                            color: 'text.secondary'
                                                        }}>
                                                            Session Chairmen:
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {session.session_category.chairmen.map(c => c.chairman_name).join(', ')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}

                                            <Divider sx={{ my: 2 }} />

                                            <Grid container alignItems="center" mb={2}>
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2" sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: theme.palette.secondary.main
                                                    }}>
                                                        <Topic fontSize="small" />
                                                        Session Topics
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                        <Typography variant="subtitle2" sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            color: theme.palette.secondary.main,
                                                            pr: 10
                                                        }}>
                                                            <Person fontSize="small" />
                                                            Speakers
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>

                                            <Grid container spacing={2}>
                                                {session.topics.map((topic) => (
                                                    <Grid item xs={12} key={topic.id}>
                                                        <Paper
                                                            elevation={0}
                                                            sx={{
                                                                p: 2,
                                                                borderRadius: 2,
                                                                backgroundColor: '#f9fafc',
                                                                borderLeft: `3px solid ${theme.palette.secondary.light}`,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: '#f0f4ff'
                                                                }
                                                            }}
                                                        >
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={12} sm={8}>
                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                                        {topic.topic}
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        color: 'text.secondary',
                                                                        mt: 0.5
                                                                    }}>
                                                                        <Schedule fontSize="small" sx={{ mr: 1 }} />
                                                                        {topic.start_time} - {topic.end_time}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={12} sm={4}>
                                                                    {topic.speaker?.participant_name && (
                                                                        <Box sx={{
                                                                            backgroundColor: '#e8f4fd',
                                                                            borderRadius: '6px',
                                                                            p: 1.5,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1
                                                                        }}>
                                                                            <Person color="primary" />
                                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                {topic.speaker.participant_name}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Paper>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        ))
                    )}
                </Box>
            </Container>

            {/* Global Styles */}
            <style jsx global>{`
                .hide-edit-buttons .edit-session-button,
                .hide-edit-buttons .MuiButton-root {
                    display: none !important;
                }
            `}</style>
        </Box>
    );
};

export default ConferenceSessionsList;